import moment, { type Moment, type MomentInput, type unitOfTime } from 'moment-timezone'

moment.tz.setDefault('Asia/Jakarta') // semua moment() otomatis ikut Jakarta

const formatTime: string = 'YYYY-MM-DD HH:mm:ss'
const formatTimeLocal: string = 'DD/MM/YYYY HH.mm'

// libur kerja
const holidays = [
  '2025-01-01', //tahun baru
  '2025-01-02', //libur perusahaan
  '2025-01-03', //libur perusahaan
  '2025-01-27', //isra miraj
  '2025-01-29', //imlek
  '2025-03-31', //idul fitri
  '2025-04-01', //idul fitri
  '2025-04-02', //idul fitri (cuti bersama)
  '2025-04-03', //idul fitri (cuti bersama)
  '2025-04-04', //idul fitri (cuti bersama)
  '2025-04-18', //wafat yesus kristus
  '2025-05-01', //hari buruh
  '2025-05-12', //waisak
  '2025-05-29', //kenaikan yesus kristus
  '2025-06-06', //idul adha
  '2025-06-27', //hijriah
  '2025-09-05', //maulid nabi
  '2025-12-25', //natal
  '2025-12-26', //libur perusahaan
  '2025-12-29', //libur perusahaan
  '2025-12-30', //libur perusahaan
  '2025-12-31', //libur perusahaan
]

// sabtu kerja
const saturdayWorks = [
  '2025-01-25',
  '2025-03-22',
  '2025-06-21',
  '2025-09-27',
  '2025-11-22',
  '2025-12-20',
]

export function getWorkDays(
  start: MomentInput | null = null,
  end: MomentInput | null = null,
): number {
  const startDate = start ? moment(start) : moment().startOf('month')
  const endDate = end ? moment(end) : moment().endOf('month')
  let workDays = 0

  while (startDate.isSameOrBefore(endDate)) {
    if (
      startDate.isoWeekday() !== 7 &&
      !isHoliday(startDate) &&
      startDate.isoWeekday() !== 6 &&
      !isSaturdayWorking(startDate)
    ) {
      workDays++
    }
    startDate.add(1, 'day')
  }

  return workDays
}

function isHoliday(date: Moment) {
  return holidays.some((holiday) => moment(holiday).isSame(date, 'day'))
}

function isSaturdayWorking(date: Moment) {
  return saturdayWorks.some((day) => moment(day).isSame(date, 'day'))
}

export function getDateTimeAgo(timestamp: MomentInput | null = null): string {
  const now = moment()
  const targetTime = timestamp ? moment(timestamp) : moment()

  const diff = now.diff(targetTime, 'seconds')

  if (diff < 60) {
    return 'beberapa detik yang lalu'
  } else if (diff < 3600) {
    const minutes = Math.floor(diff / 60)
    return `${minutes} menit yang lalu`
  } else if (diff < 86400) {
    const hours = Math.floor(diff / 3600)
    return `${hours} jam yang lalu`
  } else if (diff < 604800) {
    const days = Math.floor(diff / 86400)
    return `${days} hari yang lalu`
  } else {
    return targetTime.format('dddd, DD/MM/YYYY HH:mm:ss')
  }
}

export const setInputTime = (dateTime: string, withTime?: boolean): string => {
  if (!dateTime?.trim()) {
    if (withTime) {
      return moment().format(formatTime)
    } else {
      return moment().format('YYYY-MM-DD')
    }
  }

  if (withTime) {
    const adjustedTime: string = dateTime + '.00' // tambah detik
    return moment(adjustedTime, 'DD/MM/YYYY HH.mm.ss').format(formatTime)
  } else {
    return moment(dateTime, 'DD/MM/YYYY').format('YYYY-MM-DD')
  }
}

export const getInputTime = (dateTime: string, withTime?: boolean): string => {
  const format = withTime ? formatTimeLocal : 'DD/MM/YYYY'

  if (!dateTime?.trim()) {
    return moment().tz('Asia/Jakarta').format(format)
  }

  // Cek kalau formatnya "YYYY-MM-DD HH:mm:ss" (bukan ISO)
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateTime)) {
    // Anggap data ini dikirim dalam waktu lokal Asia/Jakarta
    return moment.tz(dateTime, formatTime, 'Asia/Jakarta').format(format)
  }

  // Default → anggap ISO UTC (contoh: 2025-09-15T06:32:00Z)
  return moment.utc(dateTime).tz('Asia/Jakarta').format(format)
}

export const formatDateTime = (time?: string, format?: string) => {
  const formatTime = format || 'DD/MM/YYYY HH:mm:ss'

  if (!time) return moment().format(formatTime)
  return moment(time).format(formatTime)
}

export const getDateDiff = (
  dateFirst: string | undefined,
  dateSecond: string | undefined,
  unit: unitOfTime.Diff = 'days',
  formatInputTime: string = '',
) => {
  const dateA = formatInputTime
    ? dateFirst
      ? moment(dateFirst, formatInputTime)
      : moment()
    : moment(dateFirst)

  const dateB = formatInputTime
    ? dateSecond
      ? moment(dateSecond, formatInputTime)
      : moment()
    : moment(dateSecond)

  return dateA.diff(dateB, unit)
}

export const getTimeText = (time: MomentInput | null = null) => {
  // pastikan time berupa moment object
  const hour = time ? moment(time).hour() : moment().hour()

  if (hour >= 5 && hour < 10) return 'pagi'
  if (hour >= 10 && hour < 14) return 'siang'
  if (hour >= 14 && hour < 17) return 'sore'
  if (hour >= 17 && hour < 20) return 'petang'
  return 'malam'
}
